<template>
    <ai-popup
        :title="`Настройка ограничений уровня доступа к подразделениям для области ${state.currentGroup.name}`"
        :bottom-buttons-enabled="true"
        :first-button-disabled="!isEditable"
        first-button-text="Выбрать"
        width="60%"
        height="75%"
        v-model:visible="state.subdivisionPopupVisible"
        @submit-click="saveChanges"
        @close-popup="closePopup"
    >
        <div class="flex gap-4 h-full">
            <local-tree-list
                ref="treeListRef"
                :meta="listMeta"
                :data-source="departments"
                :editing="treeListEditing"
            />
        </div>
    </ai-popup>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useStore } from 'vuex';
import LocalTreeList from '@/components/local-tree-list.vue';
import AiPopup from '@/components/ai-popup/ai-popup.vue';
import usersLocalStore from '@/views/administration/users/store.js';
import usersStore from '@/store/administration/users.js';
import environmentData from '@/store/enviroment-data.js';

const { state, UI_ELEMENTS_IDS } = usersLocalStore;
const { usersState } = usersStore;
const { disableUiElement } = environmentData;
const store = useStore();

const isEditable = !disableUiElement(UI_ELEMENTS_IDS.RESTRICTION);
const treeListRef = ref();
const treeListInstance = ref();
const treeListExpandAll = ref();
const childInstance = ref();
const departments = computed(() => usersState.departmentsList);
const currentGroup = computed(() => user.value.data_rights.find(group => group.group_id === state.selectedGroupId));
const currentRights = computed(
    () => user.value.data_rights.filter(right => right.group_id === state.selectedGroupId)[0].departments,
);

const user = ref({});
const currentRowId = ref();
let newDepartmentsList = null;

const treeListEditing = {
    allowUpdating: true,
};

onMounted(() => {
    user.value = state.userRequestBody;
    treeListInstance.value = treeListRef.value.treeListInstance;
    treeListInstance.value.on('cellClick', setCurrentRowId);
    treeListExpandAll.value = treeListRef.value.expandAll();
    childInstance.value = treeListRef.value.childInstance;
    onShown();
});

const recursiveStartingAccessType = department => {
    usersState.departmentsList.forEach(dep => {
        if (department.dep_id === dep.parent_id && currentRights.value.findIndex(d => d.id === dep.dep_id) === -1) {
            dep.department_permission = department.permission;
        }
    });
    usersState.departmentsList.forEach(dep => {
        if (department.dep_id === dep.parent_id) {
            recursiveStartingAccessType({ dep_id: dep.dep_id, permission: dep.department_permission });
        }
    });
};

const onShown = () => {
    treeListExpandAll.value;
    if (currentGroup.value.departments === null) {
        usersState.departmentsList.forEach(department => {
            department.department_permission = 'W';
        });
    } else {
        usersState.departmentsList.forEach(department => {
            currentRights.value.forEach(dep => {
                if (department.dep_id === dep.id) {
                    department.department_permission = dep.permission;
                }
            });
            if (!department?.department_permission) {
                department.department_permission = 'N';
            }
        });
        usersState.departmentsList.forEach(department => {
            if (department.parent_id === null) {
                recursiveStartingAccessType({
                    dep_id: department.dep_id,
                    permission: department.department_permission,
                });
            }
        });
    }
};

const closePopup = () => {
    state.userCardPopupVisible = true;
};

let sd = [];

const recursiveChechAccessType = department => {
    newDepartmentsList.forEach(dep => {
        if (department.dep_id === dep.parent_id) {
            recursiveChechAccessType({
                dep_id: dep.dep_id,
                parent_id: dep.parent_id,
                permission: dep.department_permission,
                parent_permission: department.permission,
            });
        }
    });

    if (
        (department.parent_id !== null && department.permission !== department.parent_permission) ||
        (department.parent_id === null && department.permission !== 'N')
    ) {
        sd.push({ id: department.dep_id, permission: department.permission });
    }

    return department;
};

const saveChanges = () => {
    let saveData = [];
    let countParents = 0;
    let countFullParents = 0;
    newDepartmentsList = usersState.departmentsList;

    newDepartmentsList.forEach(department => {
        if (department.parent_id === null) {
            sd = [];
            const dep = recursiveChechAccessType({
                dep_id: department.dep_id,
                parent_id: null,
                permission: department.department_permission,
                parent_permission: null,
            });
            if (sd.length === 1 && dep.dep_id === department.dep_id && dep.permission === 'W') {
                ++countFullParents;
            }
            ++countParents;
            saveData.push(...sd);
        }
    });
    if (saveData.length === 0) {
        newDepartmentsList.forEach(dep => {
            if (dep.parent_id === null) {
                saveData.push({ id: dep.dep_id, permission: dep.department_permission });
            }
        });
    } else if (countFullParents === countParents && saveData.lenght > 0) {
        saveData = null;
    }

    const currentGroupIndex = user.value.data_rights.findIndex(group => group.group_id === state.selectedGroupId);
    state.userRequestBody.data_rights[currentGroupIndex].departments = saveData;

    state.subdivisionPopupVisible = false;
    closePopup();
};

const setCurrentRowId = event => {
    currentRowId.value = event.key;
};

const recursiveLookupChanges = e => {
    usersState.departmentsList.forEach(department => {
        if (department.parent_id === e.currentRowId) {
            recursiveLookupChanges({ currentRowId: department.dep_id, value: e.value });
        }
    });
    usersState.departmentsList.find(department => {
        if (department.dep_id === e.currentRowId) {
            department.department_permission = e.value;
            return true;
        }
        return false;
    });
};

const permissionType = [
    { id: 'N', name: 'Нет доступа' },
    { id: 'R', name: 'Чтение' },
    { id: 'W', name: 'Запись' },
];

const listMeta = computed(() => ({
    keyExpr: 'dep_id',
    wordWrapEnabled: true,
    parentIdExpr: 'parent_id',
    rootValue: 'NULL',
    scrollingMode: 'standart',
    selectionMode: 'none',
    columns: [
        {
            dataField: 'dep_id',
            caption: 'ID',
            visible: false,
            showInColumnChooser: false,
            dataType: 'number',
        },
        {
            dataField: 'name',
            caption: 'Название подразделения',
            dataType: 'string',
            allowEditing: false,
            width: '49%',
        },
        {
            dataField: 'department_permission',
            caption: 'Тип доступа',
            width: '17%',
            allowEditing: isEditable,
            editorOptions: {
                onValueChanged: e => {
                    e.currentRowId = currentRowId.value;
                    recursiveLookupChanges(e);
                },
            },
            lookup: {
                dataSource: permissionType,
                valueExpr: 'id',
                displayExpr: 'name',
            },
        },
        {
            dataField: 'kod_separator',
            caption: 'Уровень иерархии',
            allowEditing: false,
            width: '17%',
            lookup: {
                dataSource: store.state.counteragentNsi.orgSeparator,
                valueExpr: 'kod_separator',
                displayExpr: 'name',
            },
        },
        {
            dataField: 'kod_ecls',
            caption: 'Отношение к энергосистеме',
            allowEditing: false,
            width: '17%',
            lookup: {
                dataSource: store.state.counteragentNsi.cls,
                valueExpr: 'kod_ecls',
                displayExpr: 'name',
            },
        },
    ],
}));
</script>
